import React, { useEffect, useState } from 'react';
import Logo from './Logo';
import CookieConsentBanner from './CookieConsentBanner';
import DevopsLeadForm from './DevopsLeadForm';
import { readCookieConsent } from './cookieConsentStorage';
import { loadYandexMetrika } from './yandexMetrika';
import { COMPANY } from './data/siteData';
import {
    DEVOPS_AUDIENCE,
    DEVOPS_HERO_STEPS,
    DEVOPS_NAV,
    DEVOPS_PROCESS,
    DEVOPS_SERVICES,
} from './data/devopsLanding';

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const DevopsPage = () => {
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const consent = readCookieConsent();
        if (consent?.analytics) {
            loadYandexMetrika();
        }
    }, []);

    const handleNav = (id) => {
        setMenuOpen(false);
        scrollTo(id);
    };

    return (
        <>
            <CookieConsentBanner />

            <div className="page devops-page">
                <header className="site-header">
                    <a href="/" className="logo-link" aria-label="Betuple на главную">
                        <Logo />
                    </a>
                    <nav className="site-nav" aria-label="Навигация по DevOps-услугам">
                        {DEVOPS_NAV.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="site-nav__link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleNav(item.id);
                                }}
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                    <button
                        type="button"
                        className="button button--primary button--header"
                        onClick={() => handleNav('lead')}
                    >
                        Разбор контура
                    </button>
                    <button
                        type="button"
                        className={`burger ${menuOpen ? 'burger--open' : ''}`}
                        aria-label="Меню"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <span />
                        <span />
                    </button>
                </header>

                {menuOpen && (
                    <nav className="mobile-nav" aria-label="Мобильное меню">
                        {DEVOPS_NAV.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="mobile-nav__link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleNav(item.id);
                                }}
                            >
                                {item.label}
                            </a>
                        ))}
                        <button
                            type="button"
                            className="button button--primary"
                            onClick={() => handleNav('lead')}
                        >
                            Разбор контура
                        </button>
                    </nav>
                )}

                <main>
                    <section className="hero hero--devops">
                        <div className="hero__content">
                            <p className="eyebrow">ООО «Битапл» · betuple.com/devops</p>
                            <h1 className="hero__title">
                                DevOps-услуги:
                                <br />
                                Kubernetes, CI/CD, облако
                            </h1>
                            <p className="hero__subtitle">
                                Ставим и сопровождаем инфраструктуру, чтобы релизы шли через пайплайн,
                                а прод не держался на ручных правках с сервера.
                            </p>
                            <div className="hero__actions">
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={() => scrollTo('lead')}
                                >
                                    Разбор текущего контура
                                </button>
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={() => scrollTo('process')}
                                >
                                    Как работаем
                                </button>
                            </div>
                        </div>
                        <div className="hero__visual" aria-hidden="true">
                            <div className="hero-panel">
                                <header className="hero-panel__head">
                                    <span className="hero-panel__kicker">Старт</span>
                                    <span className="hero-panel__meta">Без оплаты «за бренд»</span>
                                </header>
                                <ol className="hero-panel__steps">
                                    {DEVOPS_HERO_STEPS.map((step) => (
                                        <li key={step.num} className="hero-panel__step">
                                            <span className="hero-panel__num">{step.num}</span>
                                            <div>
                                                <p className="hero-panel__step-title">{step.title}</p>
                                                <p className="hero-panel__step-text">{step.text}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>

                    <section id="skip" className="section devops-audience">
                        <div className="audience-grid">
                            <article className="audience-card audience-card--yes">
                                <span className="audience-card__badge">Для кого</span>
                                <h2 className="audience-card__title">{DEVOPS_AUDIENCE.yes.title}</h2>
                                <p className="audience-card__intro">{DEVOPS_AUDIENCE.yes.intro}</p>
                                <ul className="audience-card__list">
                                    {DEVOPS_AUDIENCE.yes.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </article>
                            <article className="audience-card audience-card--no">
                                <span className="audience-card__badge">Не берём</span>
                                <h2 className="audience-card__title">{DEVOPS_AUDIENCE.no.title}</h2>
                                <p className="audience-card__intro">{DEVOPS_AUDIENCE.no.intro}</p>
                                <ul className="audience-card__list">
                                    {DEVOPS_AUDIENCE.no.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </article>
                        </div>
                    </section>

                    <section id="services" className="section">
                        <div className="section__head">
                            <p className="eyebrow">Чем занимаемся</p>
                            <h2 className="section__title">Стек, который сами держим в проде</h2>
                        </div>
                        <div className="devops-services">
                            {DEVOPS_SERVICES.map((service) => (
                                <article key={service.title} className="devops-service">
                                    <span className="devops-service__accent" aria-hidden="true" />
                                    <h3 className="devops-service__title">{service.title}</h3>
                                    <p className="devops-service__text">{service.text}</p>
                                </article>
                            ))}
                        </div>
                    </section>

                    <section id="process" className="section">
                        <div className="section__head">
                            <p className="eyebrow">Как работаем</p>
                            <h2 className="section__title">Сначала контур, потом договор</h2>
                        </div>
                        <ol className="devops-process">
                            {DEVOPS_PROCESS.map((step) => (
                                <li key={step.num} className="devops-step">
                                    <span className="devops-step__num">{step.num}</span>
                                    <h3 className="devops-step__title">{step.title}</h3>
                                    <p className="devops-step__text">{step.text}</p>
                                </li>
                            ))}
                        </ol>
                    </section>

                    <section id="lead" className="section devops-lead">
                        <div className="devops-lead__copy">
                            <p className="eyebrow">Заявка</p>
                            <h2 className="section__title">Разбор текущего контура</h2>
                            <p className="section__lead">
                                20–30 минут: что за продукт, где крутится, что болит. Не тур по слайдам.
                                Письмо — на {COMPANY.email}, договор и счета — только от {COMPANY.name}.
                            </p>
                            <ul className="devops-lead__facts">
                                <li>ИНН {COMPANY.inn}</li>
                                <li>ОГРН {COMPANY.ogrn}</li>
                                <li>Москва, Ленинская Слобода, 17</li>
                            </ul>
                        </div>
                        <DevopsLeadForm />
                    </section>
                </main>

                <footer className="site-footer">
                    <div className="site-footer__brand">
                        <a href="/" className="logo-link" aria-label="Betuple на главную">
                            <Logo />
                        </a>
                        <p className="site-footer__legal">
                            {COMPANY.name}
                            <br />
                            {COMPANY.address}
                            <br />
                            ИНН {COMPANY.inn} · ОГРН {COMPANY.ogrn}
                        </p>
                    </div>
                    <div className="site-footer__links">
                        <a href="/">Продукты Betuple</a>
                        <a href="https://marginlayer.app/ru" target="_blank" rel="noopener noreferrer">
                            MarginLayer
                        </a>
                        <a href="https://devopsfort.ru/" target="_blank" rel="noopener noreferrer">
                            DevOpsFort
                        </a>
                        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                        <a href="/conf-policy/">Политика конфиденциальности</a>
                        <a href="/personal-data/">Обработка персональных данных</a>
                    </div>
                </footer>
            </div>
        </>
    );
};

export default DevopsPage;
